import React, { useEffect, useMemo, useState } from "react";
import { FaClock, FaShieldAlt } from "react-icons/fa";
import TimeoutModal from "../../components/Modal/Timeout";
import styles from "./styles.module.css";

interface Props {
  visible: boolean;
  secondsLeft: number;
  onExtend: () => void;
  onLogout: () => void;
}

const SessionTimeoutModal: React.FC<Props> = ({
  visible,
  secondsLeft: initialSecondsLeft,
  onExtend,
  onLogout,
}) => {
  const [secondsLeft, setSecondsLeft] = useState(initialSecondsLeft);

  useEffect(() => {
    if (!visible) return;

    setSecondsLeft(initialSecondsLeft);

    const interval = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(interval);
          onLogout();
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(interval);
  }, [visible, initialSecondsLeft, onLogout]);

  const progress = useMemo(() => {
    if (!initialSecondsLeft) return 0;

    return Math.max(0, Math.min(100, (secondsLeft / initialSecondsLeft) * 100));
  }, [secondsLeft, initialSecondsLeft]);

  if (!visible) return null;

  return (
    <TimeoutModal
      isVisible={visible}
      size="md"
      theme="dark"
      trapFocus
      autoFocus
      content={
        <div className={styles.timeoutContainer}>
          <div className={styles.brandLogo} aria-label="Upcoma Entertainment">
            <div className={styles.logoText}>
              <span className={styles.logoOrange}>Up</span>
              <span className={styles.logoWhite}>coma</span>
            </div>
            <span className={styles.logoSubText}>Entertainment</span>
          </div>

          <div className={styles.timeoutIconWrap}>
            <div
              className={styles.progressRing}
              style={{
                background: `conic-gradient(#ff4b2b ${progress}%, rgba(255,255,255,0.1) ${progress}%)`,
              }}
            >
              <div className={styles.progressInner}>
                <FaClock />
              </div>
            </div>
          </div>

          <div className={styles.content}>
            <span className={styles.badge}>
              <FaShieldAlt />
              Secure session notice
            </span>

            <h3>Your session is about to expire</h3>

            <p>
              You will be logged out in{" "}
              <strong>
                {secondsLeft} second{secondsLeft === 1 ? "" : "s"}
              </strong>
              . Extend your session to continue managing the admin portal.
            </p>
          </div>

          <div className={styles.actionGrid}>
            <button
              type="button"
              onClick={onLogout}
              className={`${styles.actionButton} ${styles.logoutButton}`}
            >
              Logout
            </button>

            <button
              type="button"
              onClick={onExtend}
              className={`${styles.actionButton} ${styles.extendButton}`}
              autoFocus
            >
              Extend Session
            </button>
          </div>
        </div>
      }
    />
  );
};

export default SessionTimeoutModal;
